import classnames from 'classnames';
import { useTranslation } from 'next-i18next';
import { HTMLInputTypeAttribute } from 'react';
import { Control, FieldValues, MultipleFieldErrors, useController } from 'react-hook-form';
import { displayError } from '../../../core/helpers/yup';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Props<Values extends FieldValues = any> = {
  placeholder?: string;
  control: Control<Values, unknown>;
  name: string;
  label?: string;
  labelClassName?: string;
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
  showError?: boolean;
  size?: 'small' | 'medium' | 'large';
  background?: 'white' | 'gray';
  maxLength?: number;
  border?: boolean;
  type?: HTMLInputTypeAttribute;
  requirements?: string[];
  autoComplete?: string;
  /**
   * see: https://www.w3.org/TR/uievents-key/#named-key-attribute-values
   */
  onKeyUp?: (keyValue: string) => void;
  onBlur?: () => void;
};

const FormInput = ({
  placeholder,
  className,
  inputClassName,
  disabled,
  control,
  label,
  labelClassName,
  showError = true,
  size = 'small',
  background = 'white',
  maxLength,
  name,
  onKeyUp,
  onBlur,
  border = true,
  type = 'text',
  autoComplete = 'on',
  requirements = [],
}: Props) => {
  const {
    field,
    fieldState: { error },
  } = useController({ name, control });
  const { i18n, t } = useTranslation('error');

  const messages = Array.from(
    new Set(error ? Object.values(error?.types as MultipleFieldErrors) : [])
  );

  return (
    <div className={`flex flex-col ${className}`}>
      {label && (
        <div className="flex justify-between">
          <label className={classnames('mb-2 text-small', labelClassName)}>{label}</label>
        </div>
      )}
      <input
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...field}
        className={classnames(
          'focus:shadow-outline w-full appearance-none rounded px-3 py-1 text-small leading-tight focus:outline-none',
          inputClassName,
          {
            border: border || error,
            'border-red-700': error,
            'bg-red-100': error,
            'h-8': size === 'small',
            'h-10': size === 'medium',
            'h-12': size === 'large',
            'border-gray-200': !error,
            'bg-white': !error && background === 'white',
            'bg-gray-50': !error && background === 'gray',
          }
        )}
        type={type}
        maxLength={maxLength}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={autoComplete}
        onBlur={
          onBlur
            ? () => {
                onBlur();
              }
            : undefined
        }
        onKeyUp={
          onKeyUp
            ? (e) => {
                onKeyUp(e.key);
              }
            : undefined
        }
      />
      {requirements.length > 0 &&
        requirements.map((requirement) => (
          <p
            className={classnames('mt-1 text-mini', {
              'text-red-700': messages.includes(requirement),
            })}
            key={requirement as string}
          >
            {displayError(requirement, i18n, t)}
          </p>
        ))}
      {messages &&
        showError &&
        Array.from(messages).map(
          (message, index) =>
            !requirements.includes(message as string) && (
              <p
                data-testid={`${name}_error_${index}`}
                className="mt-1 text-mini text-red-700"
                key={message as string}
              >
                {displayError(message, i18n, t)}
              </p>
            )
        )}
    </div>
  );
};

FormInput.displayName = 'FormInput';

export default FormInput;
